import React, { useEffect, useMemo, useRef, useState } from "react"
import moment from "moment-timezone"
import { Map as MapIcon, CreditCard, FileText, ShieldCheck, CalendarClock, RefreshCw, Mail, Phone, Lock, ArrowRight, Flag, Plane, Car, BedDouble, BadgePercent } from "lucide-react"

import { db } from "../../utils/firebase"
import { computeQuoteDeposit } from "../../utils/quotes/deposit"
import { ItineraryTimeline, PreviewImage } from "../enquiry/_components/new-quote/itinerary-timeline"
import Logo from "../../assets/images/ghd-logo-wordmark.png"
import Loading from "../../components/app/loading/loading"
import QuoteTerms from "../../components/quote-terms/quote-terms"
import BookModal from "./book-modal"
import TrustpilotMicroCombo from "./trustpilot-micro-combo"
import PaymentMethodsRail from "./payment-methods-rail"

import "./public-quote.scss"

/**
 * Pin every moment(...) in the public quote to UK time (matching the
 * "manage my booking" portal) so clients abroad see the same times the UK
 * office entered. Module-load is fine: this module only evaluates when a quote
 * page is opened.
 */
moment.tz.setDefault("Europe/London")

const gbp = (value) => `£${Math.round(Number(value) || 0).toLocaleString("en-GB")}`

// mailto for the "Contact your agent" buttons, with a helpful prefilled subject
// line carrying the quote reference so the agent can place the enquiry fast.
const agentMailto = (email, reference) => {
	if (!email) return undefined
	const subject = `Question about golf holiday quote${reference ? ` [Ref: ${reference}]` : ""}`
	return `mailto:${email}?subject=${encodeURIComponent(subject)}`
}

// Sticky section nav (and mobile bottom bar) targets.
const SECTIONS = [
	{ id: "overview", label: "Overview", Icon: FileText },
	{ id: "itinerary", label: "Itinerary", Icon: MapIcon },
	{ id: "payment", label: "Payment", Icon: CreditCard },
]

/**
 * Public, client-facing online quote at /quote/:token. Reads the sanitised
 * public_quotes/{token} projection (no cost / margin data) and renders a
 * "manage my booking"-styled single page: a summary hero, the sales-agent card,
 * the itinerary timeline, and a payment section with the deposit due today and
 * the "Secure Quote & Book" call to action.
 *
 * Also serves as the agent-side "client preview": pass an already-built
 * projection as `previewQuote` (and `isPreview`) and the page renders straight
 * from it instead of fetching a published doc - no DB read, no live booking
 * (the book modal is suppressed and the navigation chrome hidden). The builder
 * and the draft cards use this to show exactly what the client would see
 * without publishing anything.
 */
export default function PublicQuoteWrapper({ match, previewQuote = null, isPreview = false }) {
	const token = match?.params?.token

	const [quote, setQuote] = useState(previewQuote || null)
	const [loading, setLoading] = useState(!previewQuote)
	const [activeSection, setActiveSection] = useState("overview")
	const [booking, setBooking] = useState(false)
	const [agent, setAgent] = useState(null)
	const [drawerOpen, setDrawerOpen] = useState(false)

	// Live-load the published quote so a re-send / status change reflects without
	// the client refreshing. In preview mode we skip the fetch entirely and render
	// straight from the supplied projection, re-syncing whenever it changes.
	useEffect(() => {
		if (previewQuote) {
			setQuote(previewQuote)
			setLoading(false)
			return undefined
		}
		if (!token) {
			setLoading(false)
			return undefined
		}
		const unsubscribe = db.doc(`public_quotes/${token}`).onSnapshot(
			(snap) => {
				setQuote(snap.exists ? { id: snap.id, ...snap.data() } : null)
				setLoading(false)
			},
			(err) => {
				console.error("Could not load public quote:", err)
				setQuote(null)
				setLoading(false)
			},
		)
		return () => unsubscribe()
	}, [token, previewQuote])

	// Pull the sales agent's name + photo LIVE from their user doc (publicly
	// readable, same as the manage-my-booking portal) so it's always current.
	// Photo lives on either `profilePicture` (newer sized object) or `picture`.
	useEffect(() => {
		const agentID = quote?.agent_id
		if (!agentID) {
			setAgent(null)
			return undefined
		}
		let cancelled = false
		db.doc(`users/${agentID}`)
			.get()
			.then((snap) => {
				if (cancelled || !snap.exists) return
				const data = snap.data() || {}
				const photo = data.profilePicture?._256 || data.profilePicture?._528 || data.profilePicture?.original || data.picture || ""
				setAgent({
					name: [data.first_name, data.last_name].filter(Boolean).join(" ") || "Your sales agent",
					first_name: data.first_name || "",
					last_name: data.last_name || "",
					job_title: data.job_title || "",
					photo,
					email: data.email || "",
					phone: data.phone || data.telephone || "",
				})
			})
			.catch((err) => console.error("Could not load sales agent:", err))
		return () => {
			cancelled = true
		}
	}, [quote?.agent_id])

	// Scrollspy: highlight the section whose top has most recently scrolled past
	// the nav. A plain scroll-position check (vs an IntersectionObserver band) so
	// a tall itinerary section can't trap the indicator, and a bottom-of-page
	// guard so the final (payment) section still activates when it can't reach
	// the middle of the viewport.
	useEffect(() => {
		if (!quote) return undefined

		const updateActive = () => {
			const line = 120 // a touch below the sticky header + nav
			let current = SECTIONS[0].id
			SECTIONS.forEach(({ id }) => {
				const el = document.getElementById(id)
				if (el && el.getBoundingClientRect().top - line <= 0) current = id
			})
			const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4
			if (atBottom) current = SECTIONS[SECTIONS.length - 1].id
			setActiveSection(current)
		}

		window.addEventListener("scroll", updateActive, { passive: true })
		window.addEventListener("resize", updateActive)
		updateActive()
		return () => {
			window.removeEventListener("scroll", updateActive)
			window.removeEventListener("resize", updateActive)
		}
	}, [quote])

	const scrollToSection = (id) => {
		const el = document.getElementById(id)
		if (!el) return
		const top = el.getBoundingClientRect().top + window.scrollY - 78
		window.scrollTo({ top, behavior: "smooth" })
	}

	const deposit = useMemo(
		() =>
			quote
				? computeQuoteDeposit({ groupSize: quote.group_size, travelMillis: quote.travel_millis, sellTotal: quote.sell_total, flightDepositGBP: quote.flight_deposit, fullBalanceWeeks: quote.full_balance_weeks })
				: null,
		[quote],
	)

	// After a successful payment the webhook flips the quote to "booked" and the
	// live listener re-renders the page. Scroll back to the top so the green
	// "booked" banner is in view - the book modal's success screen may still be
	// open over the page, so by the time it's dismissed the banner is right there.
	const wasBookedRef = useRef(quote?.status === "booked")
	useEffect(() => {
		const isNowBooked = quote?.status === "booked"
		if (isNowBooked && !wasBookedRef.current) {
			window.scrollTo({ top: 0, behavior: "smooth" })
		}
		wasBookedRef.current = isNowBooked
	}, [quote?.status])

	if (loading) {
		return (
			<div className="pq-loading">
				<Loading />
			</div>
		)
	}

	if (!quote) {
		return (
			<div className="pq-missing">
				<img className="pq-missing-logo" src={Logo} alt="Golf Holidays Direct" />
				<h1>Quote not found</h1>
				<p>This quote link is no longer available. Please contact your sales agent for an up-to-date quote.</p>
			</div>
		)
	}

	// A cancelled quote is hidden entirely - the client only sees a friendly
	// message and a link back to the website, never the (cancelled) quote detail.
	if (quote.status === "cancelled") {
		return (
			<div className="pq-missing">
				<img className="pq-missing-logo" src={Logo} alt="Golf Holidays Direct" />
				<h1>This quote is no longer available</h1>
				<p>It looks like this quote has expired. Explore our latest golf holidays, or get in touch and we'll be happy to put together a new quote for you.</p>
				<a className="pq-cta pq-missing-cta" href="https://www.golfholidaysdirect.com" target="_blank" rel="noreferrer">
					Visit our website
					<ArrowRight size={15} />
				</a>
			</div>
		)
	}

	const nodes = Array.isArray(quote.nodes) ? quote.nodes : []
	const hotelTitle = [...new Set(quote.hotel_names || [])].join(", ") || "Your golf holiday"

	// Summary card: trip facts, "what's included" lines, and per-person price(s).
	const groupSize = Number(quote.group_size) || 0
	const tripFacts = [
		quote.travel_millis ? moment(quote.travel_millis).format("ddd D MMM YYYY") : "",
		quote.nights ? `${quote.nights} night${quote.nights === 1 ? "" : "s"}` : "",
		groupSize ? `${groupSize} golfer${groupSize === 1 ? "" : "s"}` : "",
	].filter(Boolean)
	const summaryLines = Array.isArray(quote.summary_lines) ? quote.summary_lines : []
	// Applied discounts the agent chose to advertise (display-only, set at send
	// time). Shown as an "Offers included" block under the price(s).
	const offers = Array.isArray(quote.offers) ? quote.offers : []

	// Per-person sell price(s). A split quote shows tiers (cheapest = Sharing,
	// dearest = Single occupancy); otherwise the single blended per-person price.
	const sellLines = Array.isArray(quote.sell_lines) ? quote.sell_lines : []
	const split = Boolean(quote.sell_split) && sellLines.length > 0
	const sortedTiers = [...sellLines].sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0))
	const priceTiers = split
		? sortedTiers.map((line, index) => ({
				price: Number(line.price) || 0,
				label: sortedTiers.length === 2 ? (index === 0 ? "Sharing" : "Single occupancy") : "Per person",
		  }))
		: [{ price: Number(quote.sell_per_person) || 0, label: "Per person" }]

	// Clarifying footnote shown under the price(s) (summary ticket + payment card):
	// the per-person price is calculated for the whole party, so spell out the group
	// size. "Price is" vs "Prices are" tracks how many priced tiers are shown.
	const priceBasisNote = groupSize > 0 ? `${priceTiers.filter((tier) => tier.price > 0).length > 1 ? "Prices are" : "Price is"} based on ${groupSize} ${groupSize === 1 ? "person" : "people"}.` : ""

	// Cancelled quotes are short-circuited above, so by here it's active or booked.
	const isBooked = quote.status === "booked"
	// Past the expiry stamped at send time, every book / secure action is hidden
	// (the page itself stays viewable). Absent expiry = never expires (older
	// quotes, sent before expiries were stored).
	const isExpired = quote.expires_at ? Date.now() > Number(quote.expires_at) : false
	// Quote-only: the agent sent this as a quote with no self-serve booking, so
	// the book buttons are replaced by a "contact your sales agent" prompt.
	const quoteOnly = !isBooked && Boolean(quote.book_disabled)
	const canBook = !isBooked && !isExpired && !quoteOnly && deposit && deposit.dueToday > 0

	// One-liner shown wherever the "what's due today" line appears (ticket footer,
	// mobile drawer): the group/per-person deposit, or the full balance inside 12 weeks.
	let depositLine = ""
	if (deposit) {
		if (deposit.fullBalanceDue) {
			depositLine = `${gbp(deposit.dueToday)} due today`
		} else if (deposit.groupDeposit) {
			depositLine =
				deposit.flightDeposit > 0
					? `Secure today from ${gbp(deposit.dueToday)} (${gbp(deposit.groupSecureDeposit)} group deposit + flights)`
					: `Secure today with a ${gbp(deposit.groupSecureDeposit)} group deposit`
		} else if (deposit.flightDeposit > 0) {
			depositLine = `Secure today with a ${gbp(deposit.perPersonDueToday)} per person deposit`
		} else {
			depositLine = `Secure today with a ${gbp(deposit.dueToday)} group deposit`
		}
	}
	const agentInitials = agent ? ((agent.first_name?.[0] || "") + (agent.last_name?.[0] || "")).toUpperCase() || "GHD" : "GHD"

	// The "note from your agent" block shows on the live page only once a message
	// exists. In preview we always show it - with placeholder text when there's no
	// message yet - so the agent can see where their note will sit on the page.
	const showAgentNote = Boolean(quote.agent_message) || isPreview

	return (
		<div className="pq-shell">
			{/* Logo row (scrolls away) */}
			<header className="pq-header">
				<img className="pq-logo" src={Logo} alt="Golf Holidays Direct" />
				<TrustpilotMicroCombo />
			</header>

			{/* Floating frosted nav (desktop): section switcher + book CTA. Sticks
			    to the top as the page scrolls; hidden on mobile (the bottom pill +
			    drawer take over). Hidden in preview: it scroll-spies the window, which
			    doesn't drive the preview's own scroll container. */}
			{!isPreview && (
			<nav className="pq-floatnav">
				<div className="pq-floatnav-links">
					{SECTIONS.map(({ id, label }) => (
						<button
							key={id}
							type="button"
							className={["pq-floatnav-link", activeSection === id ? "is-active" : ""].join(" ").trim()}
							onClick={() => scrollToSection(id)}>
							{label}
						</button>
					))}
				</div>
				{canBook && (
					<button type="button" className="pq-floatnav-book" onClick={() => setBooking(true)}>
						Book this quote
						<ArrowRight size={15} />
					</button>
				)}
			</nav>
			)}

			<main className="pq-main">
				{isBooked && (
					<div className="pq-banner is-booked">
						This quote has been booked! Your sales agent will be in touch with the next steps and confirmation.
					</div>
				)}

				{/* Personal note from the agent (above the ticket). Copy on the left, a
				    stacked sales-agent card on the right. Hidden entirely on the live page
				    when the agent didn't leave a message; in preview it shows with
				    placeholder copy so the agent can see where their note will appear. */}
				{showAgentNote ? (
					<section className="pq-message">
						<div className="pq-message-note">
							<span className="pq-message-kicker">A note from {agent?.first_name || "your agent"}</span>
							{quote.agent_message ? (
								<p className="pq-message-text">{quote.agent_message}</p>
							) : (
								<p className="pq-message-text is-placeholder">Your personal message to the client will appear here.</p>
							)}
						</div>
						{agent && (
							<div className="pq-message-agent">
								<div className="pq-message-agent-avatar">{agent.photo ? <img src={agent.photo} alt={agent.name} /> : agentInitials}</div>
								<div className="pq-message-agent-meta">
									<span className="pq-message-agent-role">Your sales agent</span>
									<strong className="pq-message-agent-name">{agent.name}</strong>
									{agent.job_title ? <span className="pq-message-agent-title">{agent.job_title}</span> : null}
								</div>
								<div className="pq-message-agent-acts">
									{agent.email ? (
										<a className="pq-agent-btn" href={agentMailto(agent.email, quote.reference)}>
											<Mail size={15} />
											<span>Email</span>
										</a>
									) : null}
									{agent.phone ? (
										<a className="pq-agent-btn" href={`tel:${agent.phone}`}>
											<Phone size={15} />
											<span>Call</span>
										</a>
									) : null}
								</div>
							</div>
						)}
					</section>
				) : null}

				{/* Overview hero — option A "ticket": image stub · perforation · info.
				    The trip facts + per-person pricing breakdown are intentionally
				    LEFT BLANK for now (to be designed next; per-person price only,
				    never the group total). */}
				<section id="overview" className="pq-section">
					<div className="pq-ticket">
						<div className="pq-ticket-stub">
							<PreviewImage src={quote.hero_image} className="pq-ticket-image" iconSize={42} />
							<span className="pq-ticket-chip">Your personalised quote</span>
						</div>

						<span className="pq-ticket-perf" aria-hidden="true" />

						<div className="pq-ticket-info">
							<div className="pq-ticket-head">
								<h1 className="pq-ticket-title">{hotelTitle}</h1>
								{quote.reference ? <span className="pq-ticket-ref">Ref: {quote.reference}</span> : null}
							</div>

							<div className="pq-ticket-body">
								{tripFacts.length > 0 && <p className="pq-ticket-trip">{tripFacts.join("  ·  ")}</p>}

								{summaryLines.length > 0 && (
									<ul className="pq-ticket-summary">
										{summaryLines.map((line, index) => (
											<li key={index}>
												<span className="pq-ticket-summary-ic">
													{line.kind === "round" ? <Flag size={15} /> : line.kind === "flight" ? <Plane size={15} /> : line.kind === "board" ? <BedDouble size={15} /> : <Car size={15} />}
												</span>
												<span>{line.text}</span>
											</li>
										))}
									</ul>
								)}

								{priceTiers.some((tier) => tier.price > 0) && (
									<div className="pq-ticket-pricing">
										{priceTiers.map((tier, index) => (
											<div className="pq-ticket-price-row" key={index}>
												<span className="pq-ticket-price-label">{tier.label}</span>
												<strong className="pq-ticket-price-amount">
													{gbp(tier.price)}
													<small>pp</small>
												</strong>
											</div>
										))}
										{priceBasisNote ? <p className="pq-ticket-price-note">{priceBasisNote}</p> : null}
									</div>
								)}

								{offers.length > 0 && (
									<div className="pq-ticket-offers">
										<span className="pq-ticket-offers-title">
											<BadgePercent size={15} />
											Offers included
										</span>
										<ul className="pq-ticket-offers-list">
											{offers.map((text, index) => (
												<li key={index}>{text}</li>
											))}
										</ul>
									</div>
								)}
							</div>

							{canBook && (
								<div className="pq-ticket-foot">
									{deposit ? (
										<span className="pq-ticket-deposit">
											{deposit.fullBalanceDue ? (
												<>
													<strong>{gbp(deposit.dueToday)}</strong> due today
												</>
											) : deposit.groupDeposit ? (
												deposit.flightDeposit > 0 ? (
													<>
														Secure today from <strong>{gbp(deposit.dueToday)}</strong> ({gbp(deposit.groupSecureDeposit)} group deposit + flights)
													</>
												) : (
													<>
														Secure today with a <strong>{gbp(deposit.groupSecureDeposit)}</strong> group deposit
													</>
												)
											) : deposit.flightDeposit > 0 ? (
												<>
													Secure today with a <strong>{gbp(deposit.perPersonDueToday)}</strong> per person deposit
												</>
											) : (
												<>
													Secure today with a <strong>{gbp(deposit.dueToday)}</strong> group deposit
												</>
											)}
										</span>
									) : null}
									<button type="button" className="pq-cta" onClick={() => setBooking(true)}>
										Book this quote
										<ArrowRight size={15} />
									</button>
								</div>
							)}

							{quoteOnly && (
								<div className="pq-ticket-foot is-contact">
									<span className="pq-ticket-deposit">To book, please contact your sales agent.</span>
									{agent?.email ? (
										<a className="pq-cta" href={agentMailto(agent.email, quote.reference)}>
											Contact {agent.first_name || "your agent"}
											<ArrowRight size={15} />
										</a>
									) : null}
								</div>
							)}
						</div>
					</div>
				</section>

				{/* Sales agent - only when there's no note block above, since that block
				    already carries a (stacked) agent card. With a note (or the preview
				    placeholder), this would be a duplicate, so it's dropped. */}
				{agent && !showAgentNote && (
					<section className="pq-section pq-agent-section">
						<AgentCard agent={agent} reference={quote.reference} />
					</section>
				)}

				{/* Itinerary, set off with a hairline divider above and below. */}
				<div className="pq-divider" aria-hidden="true" />
				<section id="itinerary" className="pq-section">
					<h2 className="pq-section-title">Your itinerary</h2>
					{nodes.length > 0 ? (
						<ItineraryTimeline nodes={nodes} />
					) : (
						<p className="pq-empty">Your itinerary is being finalised - your sales agent will confirm the details shortly.</p>
					)}
				</section>
				<div className="pq-divider" aria-hidden="true" />

				{/* Payment */}
				<section id="payment" className="pq-section">
					<h2 className="pq-section-title">Payment</h2>
					<PaymentBlock deposit={deposit} canBook={canBook} quoteOnly={quoteOnly} agent={agent} reference={quote.reference} priceBasisNote={priceBasisNote} onBook={() => setBooking(true)} />
				</section>

				{/* Standing terms / scheme info (shared with the quote email). */}
				<QuoteTerms className="pq-terms" balanceWeeks={deposit?.balanceDueWeeksBefore} />
			</main>

			<footer className="pq-footer">
				<span className="pq-footer-protect">
					<ShieldCheck size={16} />
					ATOL protected (11357) · Your money is held in trust
				</span>
				<p>
					&copy; {new Date().getFullYear()} Golf Holidays Direct ·{" "}
					<a href="https://www.golfholidaysdirect.com/terms-and-conditions" target="_blank" rel="noreferrer">
						Terms &amp; Conditions
					</a>
				</p>
			</footer>

			{/* Mobile floating nav pill: section switcher + a book button that opens
			    the drawer. Hidden on desktop, and in preview (see the float nav). */}
			{!isPreview && (
			<nav className="pq-mobilenav">
				{SECTIONS.map(({ id, label }) => (
					<button
						key={id}
						type="button"
						className={["pq-mobilenav-tab", activeSection === id ? "is-active" : ""].join(" ").trim()}
						onClick={() => scrollToSection(id)}>
						{label}
					</button>
				))}
				{canBook && (
					<button type="button" className="pq-mobilenav-book" onClick={() => setDrawerOpen(true)} aria-label="Open booking options">
						<ArrowRight size={16} />
					</button>
				)}
			</nav>
			)}

			{/* Mobile slide-up drawer: section links, the sales agent, and the
			    book-now block. */}
			{!isPreview && (
			<div className={["pq-drawer-root", drawerOpen ? "is-open" : ""].join(" ").trim()}>
				<div className="pq-drawer-scrim" onClick={() => setDrawerOpen(false)} />
				<div className="pq-drawer" role="dialog" aria-modal="true" aria-hidden={!drawerOpen}>
					<div className="pq-drawer-handle" />

					<nav className="pq-drawer-links">
						{SECTIONS.map(({ id, label, Icon }) => (
							<button
								key={id}
								type="button"
								className={["pq-drawer-link", activeSection === id ? "is-active" : ""].join(" ").trim()}
								onClick={() => {
									scrollToSection(id)
									setDrawerOpen(false)
								}}>
								<span className="pq-drawer-link-ic">
									<Icon size={17} />
								</span>
								{label}
							</button>
						))}
					</nav>

					{agent && (
						<div className="pq-drawer-agent">
							<div className="pq-drawer-agent-avatar">{agent.photo ? <img src={agent.photo} alt={agent.name} /> : agentInitials}</div>
							<div className="pq-drawer-agent-text">
								<small>Your sales agent</small>
								<strong>{agent.name}</strong>
								{agent.job_title ? <span>{agent.job_title}</span> : null}
							</div>
							<div className="pq-drawer-agent-acts">
								{agent.email ? (
									<a href={agentMailto(agent.email, quote.reference)} aria-label="Email your agent">
										<Mail size={16} />
									</a>
								) : null}
								{agent.phone ? (
									<a href={`tel:${agent.phone}`} aria-label="Call your agent">
										<Phone size={16} />
									</a>
								) : null}
							</div>
						</div>
					)}

					{canBook && (
						<div className="pq-drawer-book">
							{depositLine ? <span className="pq-drawer-book-due">{depositLine}</span> : null}
							<button
								type="button"
								className="pq-drawer-book-cta"
								onClick={() => {
									setDrawerOpen(false)
									setBooking(true)
								}}>
								Book this quote
								<ArrowRight size={16} />
							</button>
						</div>
					)}
				</div>
			</div>
			)}

			{/* Never open the live payment flow in preview - the page is display-only. */}
			{booking && canBook && !isPreview && <BookModal quote={quote} deposit={deposit} onClose={() => setBooking(false)} />}
		</div>
	)
}

/** Sales-agent card - photo / initials, name, and contact links. */
function AgentCard({ agent, reference }) {
	const initials = ((agent.first_name?.[0] || "") + (agent.last_name?.[0] || "")).toUpperCase() || "GHD"

	return (
		<div className="pq-agent">
			<div className="pq-agent-avatar">{agent.photo ? <img src={agent.photo} alt={agent.name} /> : initials}</div>
			<div className="pq-agent-text">
				<span className="pq-agent-label">Your personal sales agent</span>
				<strong className="pq-agent-name">{agent.name}</strong>
				{agent.email ? <span className="pq-agent-help">{agent.email}</span> : null}
			</div>
			<div className="pq-agent-actions">
				{agent.email ? (
					<a className="pq-agent-btn" href={agentMailto(agent.email, reference)}>
						<Mail size={15} />
						<span>Email</span>
					</a>
				) : null}
				{agent.phone ? (
					<a className="pq-agent-btn" href={`tel:${agent.phone}`}>
						<Phone size={15} />
						<span>Call</span>
					</a>
				) : null}
			</div>
		</div>
	)
}

/**
 * Payment section: the deposit due today (group-deposit logic), the
 * pay-your-way reassurance, and the "Secure Quote & Book" call to action.
 */
function PaymentBlock({ deposit, canBook, quoteOnly, agent, reference, priceBasisNote, onBook }) {
	if (!deposit) return null

	const balanceWeeks = deposit.balanceDueWeeksBefore

	// The deposit-to-book pitch. Within the full-balance window there's no deposit
	// to talk about, so keep the "pay it all today" line. Otherwise it's the
	// standard £50-per-person deposit, with the group total spelled out so the
	// wording matches the amount actually charged (the online flow only ever takes
	// the per-person deposit; the £100 group-secure-first option isn't bookable
	// online, so it's no longer pitched here).
	let explainer
	if (deposit.fullBalanceDue) {
		explainer = `As your travel date is within ${balanceWeeks} weeks, the full balance is payable today to confirm your booking.`
	} else if (deposit.groupDeposit) {
		// 8+ group: a £100 group deposit secures today (credited toward the deposit),
		// the rest of the per-person deposit follows within 28 days. Flights, when
		// included, are taken in full today on top to lock the fare.
		const flightsClause =
			deposit.flightDeposit > 0
				? `, plus the full cost of your flights (${gbp(deposit.flightDepositPerPerson)} per person) so we can lock them in at today's fare`
				: ""
		explainer = `As a group of 8 or more, secure your accommodation and tee times today with a ${gbp(deposit.groupSecureDeposit)} group deposit${flightsClause}. The remaining ${gbp(deposit.perPersonDeposit)} per person deposit (${gbp(deposit.secondInstalment)} for your group) follows within ${deposit.secondInstalmentDays} days, and you can pay the balance on your own terms up until ${balanceWeeks} weeks before travel via our online portal.`
	} else if (deposit.flightDeposit > 0) {
		// Flights charged up front: spell out the £50pp deposit AND the full flight
		// cost per person, and why it's taken now (fares move, so we lock them in).
		explainer = `Today's payment is a ${gbp(deposit.perPersonDeposit)} deposit per person plus the full cost of your flights (${gbp(deposit.flightDepositPerPerson)} per person), ${gbp(deposit.dueToday)} for your group. Flight prices change daily, so we take them now to secure your flights straight away at today's fare. The remaining balance is yours to pay on your own terms up until ${balanceWeeks} weeks before travel via our online portal.`
	} else {
		explainer = `Book this quote today with a ${gbp(deposit.perPersonDeposit)} deposit per person (${gbp(deposit.dueToday)} for your group), to secure room and tee time availability and choose your own payment terms up until ${balanceWeeks} weeks before travel via our online portal.`
	}

	// Small breakdown line under the headline amount.
	let dueSub = ""
	if (!deposit.fullBalanceDue) {
		if (deposit.groupDeposit) {
			dueSub =
				deposit.flightDeposit > 0
					? `${gbp(deposit.groupSecureDeposit)} group deposit + ${gbp(deposit.flightDepositPerPerson)}pp flights`
					: `group deposit${deposit.groupSize ? ` for your group of ${deposit.groupSize}` : ""}`
		} else if (deposit.flightDeposit > 0) {
			dueSub = `${gbp(deposit.perPersonDeposit)} deposit + ${gbp(deposit.flightDepositPerPerson)} flights per person`
		} else if (deposit.perPersonDeposit > 0) {
			dueSub = `${gbp(deposit.perPersonDeposit)} per person`
		}
	}

	return (
		<div className="pq-payment">
			<div className="pq-payment-card">
				{quoteOnly ? (
					<>
						<div className="pq-payment-due">
							<span className="pq-payment-due-label">Ready to book?</span>
						</div>
						<p className="pq-payment-explainer">
							To go ahead with this quote, please contact your sales agent{agent?.first_name ? `, ${agent.first_name}` : ""} - they'll take care of everything for you.
						</p>
						<div className="pq-payment-contact-acts">
							{agent?.email ? (
								<a className="pq-cta is-block" href={agentMailto(agent.email, reference)}>
									<Mail size={17} />
									Email your agent
								</a>
							) : null}
							{agent?.phone ? (
								<a className="pq-cta is-block is-ghost" href={`tel:${agent.phone}`}>
									<Phone size={17} />
									Call {agent.phone}
								</a>
							) : null}
						</div>
					</>
				) : (
					<>
						<div className="pq-payment-due">
							<span className="pq-payment-due-label">Secure booking today.</span>
							<strong className="pq-payment-due-amount">{gbp(deposit.dueToday)}</strong>
							{dueSub ? <span className="pq-payment-due-sub">{dueSub}</span> : null}
						</div>

						<p className="pq-payment-explainer">{explainer}</p>

						{priceBasisNote ? <p className="pq-payment-price-note">{priceBasisNote}</p> : null}

						{canBook && (
							<>
								<button type="button" className="pq-cta is-block" onClick={onBook}>
									<Lock size={17} />
									Secure Quote &amp; Book
								</button>
								<PaymentMethodsRail />
							</>
						)}
					</>
				)}
			</div>

			{/* Pay-your-way reassurance / benefits */}
			<div className="pq-benefits">
				<div className="pq-benefit">
					<CalendarClock size={20} />
					<div>
						<strong>Choose your own payment terms</strong>
						<span>Spread the cost however suits you, up to {balanceWeeks} weeks before travel - with no added cost.</span>
					</div>
				</div>
				<div className="pq-benefit">
					<RefreshCw size={20} />
					<div>
						<strong>Free amendments</strong>
						<span>Plans change. Adjust the details of your trip with no admin fees.</span>
					</div>
				</div>
				<div className="pq-benefit">
					<ShieldCheck size={20} />
					<div>
						<strong>ATOL protected</strong>
						<span>Your money is held securely in trust and fully financially protected.</span>
					</div>
				</div>
			</div>
		</div>
	)
}
